// Blueprint: crap/blueprint/workspace/architecture/001-coalesce-workspace-events.md
import DiagnosticsModule from '@modules/DiagnosticsModule';
import featureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import {
  CoalesceFlushReason,
  CoalesceTimerArmed,
  RTCSMetricNames,
  RTCSMetricTags,
} from '../../metrics/rtcsMetrics';
import { logException } from './log';
import isValidUser from './isValidUser';
import requireUser from './requireUser';
import { serviceState } from './state';
import type ServiceUser from '../types/ServiceUser';

// Non-self agent.session* (CCPE-2281) share this buffer with agent.status
// only when the workspace flag is on. Session lifecycle is FIFO; status is
// last-write-wins per agentId. Flush prepares in parallel then commits
// synchronously in enqueue order (one valtio notify). High-volume queue.*
// events (CCPE-2280) share the same timer: queue.status is LWW per queueId;
// join/left stay FIFO. Cache-hit queue applies are synchronous so they share
// that notify; only refreshQueueOnCacheMiss is awaited.
const FLUSH_DELAY_MS = 1000;

export type CoalescedApply<T = unknown> = {
  prepare: () => Promise<T | undefined>;
  commit: (value: T) => void;
  persistSession?: () => void;
};

type SessionItem = {
  kind: 'session';
  agentId: string;
  prepare: () => Promise<unknown>;
  commit: (value: unknown) => void;
  persistSession?: () => void;
};

type AgentStatusItem = {
  kind: 'agentStatus';
  agentId: string;
  generation: number;
  prepare: () => Promise<unknown>;
  commit: (value: unknown) => void;
};

type QueueApply = () => void | Promise<void>;

type QueueDeltaItem = {
  kind: 'queueDelta';
  queueId: string;
  apply: QueueApply;
  // Captured at enqueue. discardStaleQueueDeltas bumps the live epoch so a
  // join/left already kicked into a flush batch cannot refresh a deleted queue.
  epoch: number;
};

type QueueStatusItem = {
  kind: 'queueStatus';
  queueId: string;
  apply: QueueApply;
  // Captured at enqueue. discardStaleQueueStatus bumps the live epoch so a
  // snapshot already kicked into a flush batch cannot overwrite delivered stats.
  epoch: number;
};

type PendingItem =
  | SessionItem
  | AgentStatusItem
  | QueueDeltaItem
  | QueueStatusItem;

const pending: PendingItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const inFlightFlushes = new Set<Promise<void>>();
const kickedBatches = new Set<PendingItem[]>();
const inFlightQueueDeltaIds = new Map<string, number>();
const agentStatusGeneration = new Map<string, number>();
const queueStatusEpoch = new Map<string, number>();
const queueDeltaEpoch = new Map<string, number>();
let flushChain: Promise<void> = Promise.resolve();

function isQueueItem(
  item: PendingItem,
): item is QueueDeltaItem | QueueStatusItem {
  return item.kind === 'queueDelta' || item.kind === 'queueStatus';
}

function currentQueueStatusEpoch(queueId: string): number {
  return queueStatusEpoch.get(queueId) ?? 0;
}

function currentQueueDeltaEpoch(queueId: string): number {
  return queueDeltaEpoch.get(queueId) ?? 0;
}

function markCount(map: Map<string, number>, id: string) {
  map.set(id, (map.get(id) ?? 0) + 1);
}

function unmarkCount(map: Map<string, number>, id: string) {
  const next = (map.get(id) ?? 1) - 1;
  if (next <= 0) {
    map.delete(id);
  } else {
    map.set(id, next);
  }
}

function isWorkspaceCoalesceEnabled() {
  return featureFlagModule.isEnabled(
    FeatureFlagKeys.featureCoalesceWorkspaceEvents,
  );
}

function armFlushTimer() {
  flushTimer ??= setTimeout(() => {
    kickFlush();
  }, FLUSH_DELAY_MS);
}

function peekCoalescedAgentStatusGeneration(agentId: string): number {
  return agentStatusGeneration.get(agentId) ?? 0;
}

function isCoalescedAgentStatusInvalidated(
  agentId: string,
  generation: number,
): boolean {
  return peekCoalescedAgentStatusGeneration(agentId) !== generation;
}

function invalidateCoalescedAgentStatus(agentId: string) {
  agentStatusGeneration.set(
    agentId,
    peekCoalescedAgentStatusGeneration(agentId) + 1,
  );
}

function wrapStatusApply<T>(
  agentId: string,
  steps: CoalescedApply<T>,
): Pick<AgentStatusItem, 'generation' | 'prepare' | 'commit'> {
  const generation = peekCoalescedAgentStatusGeneration(agentId);
  return {
    generation,
    prepare: async () => {
      if (isCoalescedAgentStatusInvalidated(agentId, generation)) {
        return undefined;
      }
      return steps.prepare();
    },
    commit: (value: unknown) => {
      if (value === undefined) {
        return;
      }
      if (isCoalescedAgentStatusInvalidated(agentId, generation)) {
        return;
      }
      steps.commit(value as T);
    },
  };
}

function wrapSessionApply<T>(
  steps: CoalescedApply<T>,
): Pick<SessionItem, 'prepare' | 'commit' | 'persistSession'> {
  return {
    prepare: () => steps.prepare(),
    commit: (value: unknown) => {
      if (value === undefined) {
        return;
      }
      steps.commit(value as T);
    },
    persistSession: steps.persistSession,
  };
}

function toSteps<T>(
  apply: (() => Promise<void>) | CoalescedApply<T>,
): CoalescedApply<T> {
  if (typeof apply === 'function') {
    return {
      prepare: async () => {
        await apply();
        return undefined;
      },
      commit: () => {},
    };
  }
  return apply;
}

function applyImmediate<T>(
  apply: (() => Promise<void>) | CoalescedApply<T>,
): Promise<void> | void {
  if (typeof apply === 'function') {
    return apply();
  }
  return apply.prepare().then((prepared) => {
    if (prepared !== undefined) {
      apply.commit(prepared);
    }
  });
}

/** True while a join/left is waiting, already in a kicked flush batch, or applying. */
export function hasPendingQueueDelta(queueId: string): boolean {
  return (
    pending.some(
      (item) => item.kind === 'queueDelta' && item.queueId === queueId,
    ) || inFlightQueueDeltaIds.has(queueId)
  );
}

/**
 * Drops the buffered status for one agent on this coalescer (2281-on path).
 * Session enqueue also drops: a pre-session snapshot is stale.
 * Callers that must also clear the CCPE-1920 Map should use
 * `handleAgentStatus.dropPendingAgentStatus`.
 */
export function dropCoalescedPendingAgentStatus(agentId: string) {
  dropPendingByKind(agentId, 'agentStatus');
  invalidateCoalescedAgentStatus(agentId);
}

function dropPendingByKind(agentId: string, kind: 'session' | 'agentStatus') {
  for (let i = pending.length - 1; i >= 0; i--) {
    const item = pending[i];
    if (item.kind === kind && item.agentId === agentId) {
      pending.splice(i, 1);
    }
  }
}

function takeSessionsForAgent(
  items: PendingItem[],
  agentId: string,
): SessionItem[] {
  const taken: SessionItem[] = [];
  for (let i = 0; i < items.length; ) {
    const item = items[i];
    if (item.kind === 'session' && item.agentId === agentId) {
      taken.push(item);
      items.splice(i, 1);
    } else {
      i++;
    }
  }
  return taken;
}

function recordCoalesceFlush(
  reason: (typeof CoalesceFlushReason)[keyof typeof CoalesceFlushReason],
  batchSize: number,
  timerArmed?: boolean,
) {
  try {
    const tags: Record<string, string> = {
      [RTCSMetricTags.COALESCE_FLUSH_REASON]: reason,
    };
    if (reason === CoalesceFlushReason.DRAIN && timerArmed != null) {
      tags[RTCSMetricTags.COALESCE_TIMER_ARMED] = timerArmed
        ? CoalesceTimerArmed.TRUE
        : CoalesceTimerArmed.FALSE;
    }
    DiagnosticsModule.incrementCounter(
      RTCSMetricNames.RTCS_WORKSPACE_COALESCE_FLUSH_TOTAL,
      tags,
    );
    DiagnosticsModule.recordHistogram(
      RTCSMetricNames.RTCS_WORKSPACE_COALESCE_FLUSH_SIZE,
      batchSize,
      tags,
    );
  } catch {
    // Metrics must not break flush.
  }
}

function recordCoalesceDrainWait(waitMs: number) {
  try {
    DiagnosticsModule.recordHistogram(
      RTCSMetricNames.RTCS_WORKSPACE_COALESCE_DRAIN_WAIT_DURATION,
      waitMs,
    );
  } catch {
    // Metrics must not break drain.
  }
}

function persistBufferedSessionStores() {
  for (const item of pending) {
    if (item.kind !== 'session' || !item.persistSession) {
      continue;
    }
    try {
      item.persistSession();
    } catch (err) {
      logException(err, 'rtcs:agent:session:persist');
    }
  }
}

export function dropPendingQueueStatus(queueId: string) {
  for (let i = pending.length - 1; i >= 0; i--) {
    const item = pending[i];
    if (item.kind === 'queueStatus' && item.queueId === queueId) {
      pending.splice(i, 1);
    }
  }
}

function dropPendingQueueDeltas(queueId: string) {
  for (let i = pending.length - 1; i >= 0; i--) {
    const item = pending[i];
    if (item.kind === 'queueDelta' && item.queueId === queueId) {
      pending.splice(i, 1);
    }
  }
}

/** Drop pending snapshots and skip any already-kicked snapshot for this queue. */
export function discardStaleQueueStatus(queueId: string) {
  dropPendingQueueStatus(queueId);
  queueStatusEpoch.set(queueId, currentQueueStatusEpoch(queueId) + 1);
}

/** Drop pending join/left and skip kicked ones so they cannot refresh a deleted queue. */
export function discardStaleQueueDeltas(queueId: string) {
  dropPendingQueueDeltas(queueId);
  queueDeltaEpoch.set(queueId, currentQueueDeltaEpoch(queueId) + 1);
  // kickFlush already marked these. Delete so a post-recreate snapshot is not
  // dropped; skipped apply must not unmark a later join's kickFlush count.
  inFlightQueueDeltaIds.delete(queueId);
}

function clearPendingBuffer() {
  if (flushTimer) {
    clearTimeout(flushTimer);
  }
  flushTimer = null;
  pending.length = 0;
}

export async function resetPendingWorkspaceEvents(): Promise<void> {
  persistBufferedSessionStores();
  clearPendingBuffer();
  await Promise.allSettled([...inFlightFlushes]);
  persistBufferedSessionStores();
  clearPendingBuffer();
}

function isStatusItem(item: PendingItem): item is AgentStatusItem {
  return item.kind === 'agentStatus';
}

function flushLogContext(item: PendingItem) {
  if (item.kind === 'session') {
    return 'rtcs:agent:session:flush';
  }
  if (item.kind === 'agentStatus') {
    return 'rtcs:agent:status:flush';
  }
  return 'rtcs:queue:flush';
}

function shouldDropStatusItem(item: PendingItem): boolean {
  return (
    isStatusItem(item) &&
    (item.agentId === serviceState.contactCenterUserId ||
      isCoalescedAgentStatusInvalidated(item.agentId, item.generation))
  );
}

async function prepareFlushSlot(item: PendingItem) {
  try {
    if (shouldDropStatusItem(item) || isQueueItem(item)) {
      return null;
    }
    const prepared = await item.prepare();
    return { item, prepared };
  } catch (err) {
    logException(err, flushLogContext(item));
    return null;
  }
}

function commitFlushSlot(
  slot: { item: PendingItem; prepared: unknown } | null,
) {
  if (!slot || shouldDropStatusItem(slot.item)) {
    return;
  }
  try {
    if (slot.item.kind !== 'session' && slot.item.kind !== 'agentStatus') {
      return;
    }
    slot.item.commit(slot.prepared);
  } catch (err) {
    logException(err, flushLogContext(slot.item));
  }
}

function finishQueueApply(
  item: QueueDeltaItem | QueueStatusItem,
  onDone?: () => void,
): void | Promise<void> {
  try {
    const pendingApply = item.apply();
    if (pendingApply) {
      return pendingApply.then(
        () => {
          onDone?.();
        },
        (err: unknown) => {
          onDone?.();
          logException(err, flushLogContext(item));
        },
      );
    }
    onDone?.();
  } catch (err) {
    onDone?.();
    logException(err, flushLogContext(item));
  }
}

function applyQueueItem(
  item: QueueDeltaItem | QueueStatusItem,
): void | Promise<void> {
  try {
    if (item.kind === 'queueDelta') {
      // discardStaleQueueDeltas already deleted this epoch's mark. Unmarking
      // a skipped item would decrement a later join's kickFlush mark.
      const unmarkIfCurrentEpoch = () => {
        if (item.epoch === currentQueueDeltaEpoch(item.queueId)) {
          unmarkCount(inFlightQueueDeltaIds, item.queueId);
        }
      };
      if (item.epoch !== currentQueueDeltaEpoch(item.queueId)) {
        return;
      }
      dropPendingQueueStatus(item.queueId);
      return finishQueueApply(item, unmarkIfCurrentEpoch);
    }

    if (
      !hasPendingQueueDelta(item.queueId) &&
      item.epoch === currentQueueStatusEpoch(item.queueId)
    ) {
      return finishQueueApply(item);
    }
  } catch (err) {
    logException(err, flushLogContext(item));
  }
}

async function flushPending(batch: PendingItem[]) {
  const slots = await Promise.all(batch.map(prepareFlushSlot));
  for (let i = 0; i < batch.length; i++) {
    const item = batch[i];
    if (isQueueItem(item)) {
      const pendingApply = applyQueueItem(item);
      if (pendingApply) {
        await pendingApply;
      }
    } else {
      commitFlushSlot(slots[i]);
    }
  }
}

function enqueueFlush(run: Promise<void>, batch?: PendingItem[]) {
  if (batch) {
    kickedBatches.add(batch);
  }
  flushChain = run.then(
    () => undefined,
    () => undefined,
  );
  inFlightFlushes.add(run);
  void run.finally(() => {
    if (batch) {
      kickedBatches.delete(batch);
    }
    inFlightFlushes.delete(run);
  });
}

function kickFlush() {
  flushTimer = null;
  const batch = pending.splice(0, pending.length);
  if (batch.length === 0) {
    return;
  }

  recordCoalesceFlush(CoalesceFlushReason.TIMER, batch.length);
  // Mark before flushChain so delivered still sees a join sitting behind
  // earlier items in this batch (or behind the previous flush).
  for (const item of batch) {
    if (item.kind === 'queueDelta') {
      markCount(inFlightQueueDeltaIds, item.queueId);
    }
  }
  enqueueFlush(
    flushChain.then(() => flushPending(batch)),
    batch,
  );
}

function agentHasKickedSession(agentId: string): boolean {
  for (const batch of kickedBatches) {
    if (
      batch.some((item) => item.kind === 'session' && item.agentId === agentId)
    ) {
      return true;
    }
  }
  return false;
}

async function drainSessionsForAgent(
  sessions: SessionItem[],
  waitedOnKick: boolean,
  timerArmed: boolean,
): Promise<void> {
  const waitStart = Date.now();
  const run = flushChain.then(async () => {
    if (waitedOnKick) {
      recordCoalesceDrainWait(Date.now() - waitStart);
    }
    if (sessions.length === 0) {
      return;
    }
    recordCoalesceFlush(CoalesceFlushReason.DRAIN, sessions.length, timerArmed);
    await flushPending(sessions);
  });
  // Same kickedBatches set as a timer kick so a sibling wrapup/away/ready in
  // this receive waits on this drain instead of applying immediately.
  enqueueFlush(run, sessions.length > 0 ? sessions : undefined);
  await run;
}

/**
 * Apply this agent's buffered session events now (after any already-kicked
 * flush finishes, which includes kicked work for this agent), then return
 * so wrapup / away / ready / external can run. Does not drop those events.
 *
 * Sessions are taken off `pending` immediately so a timer kick cannot splice
 * them onto a later chained flush while this drain waits. The drain itself is
 * tracked in `inFlightFlushes` so close/resync wait for it.
 *
 * Returns void when there is nothing to wait on so flag-off / self siblings
 * stay hop-equivalent to master. `await undefined` still yields a microtask.
 */
export function drainPendingAgentSessionEvents(
  agentId: string,
): void | Promise<void> {
  if (!isWorkspaceSessionCoalesceEnabled()) {
    return;
  }
  const timerArmed = flushTimer != null;
  const hasKicked = agentHasKickedSession(agentId);
  const sessions = takeSessionsForAgent(pending, agentId);
  if (sessions.length === 0 && !hasKicked) {
    return;
  }
  return drainSessionsForAgent(sessions, hasKicked, timerArmed);
}

function isWorkspaceSessionCoalesceEnabled() {
  return (
    featureFlagModule.isEnabled(
      FeatureFlagKeys.featureCoalesceWorkspaceEvents,
    ) &&
    featureFlagModule.isEnabled(
      FeatureFlagKeys.featureCoalesceAgentStatusUpdates,
    )
  );
}

/**
 * Flag-off / self / unknown-user: same hop count as master (`await requireUser`
 * then write in this function). `{ prepare, commit }` is only for the buffered
 * non-self flush — `await prepare()` then `commit` adds a microtask and can
 * reorder vs wrapup / away / ready in one SignalR receive.
 */
export async function applyAgentSessionNow(
  agentId: string,
  commit: (user: ServiceUser) => void,
): Promise<void> {
  if (!isValidUser(agentId)) {
    return;
  }
  const user = await requireUser(agentId);
  commit(user);
}

export function shouldBufferNonSelfAgentSession(agentId: string): boolean {
  const contactCenterUserId = serviceState.contactCenterUserId;
  return (
    isWorkspaceSessionCoalesceEnabled() &&
    contactCenterUserId != null &&
    agentId !== contactCenterUserId
  );
}

/**
 * Applies immediately when the workspace flag is off, contactCenterUserId is
 * unknown, or the event is for this agent. Otherwise buffers until the ~1s
 * flush. Session buffering requires the status flag as well.
 *
 * Immediate session handlers should use `applyAgentSessionNow`, not this
 * enqueue, so commit is not an extra await past `requireUser`.
 */
export function enqueueNonSelfAgentSessionEvent(
  agentId: string,
  apply: () => Promise<void>,
): Promise<void> | void;
export function enqueueNonSelfAgentSessionEvent<T>(
  agentId: string,
  apply: CoalescedApply<T>,
): Promise<void> | void;
export function enqueueNonSelfAgentSessionEvent<T>(
  agentId: string,
  apply: (() => Promise<void>) | CoalescedApply<T>,
): Promise<void> | void {
  if (!shouldBufferNonSelfAgentSession(agentId)) {
    return applyImmediate(apply);
  }

  const steps = toSteps(apply);
  dropCoalescedPendingAgentStatus(agentId);
  pending.push({
    kind: 'session',
    agentId,
    ...wrapSessionApply(steps),
  });
  armFlushTimer();
}

/**
 * Shared-flusher status path (workspace flag on). Last-write-wins per agentId.
 * When the workspace flag is off, handleAgentStatus uses the CCPE-1920 Map.
 */
export function enqueueAgentStatusEvent(
  agentId: string,
  apply: () => Promise<void>,
): Promise<void> | void;
export function enqueueAgentStatusEvent<T>(
  agentId: string,
  apply: CoalescedApply<T>,
): Promise<void> | void;
export function enqueueAgentStatusEvent<T>(
  agentId: string,
  apply: (() => Promise<void>) | CoalescedApply<T>,
): Promise<void> | void {
  if (!shouldBufferNonSelfAgentSession(agentId)) {
    return applyImmediate(apply);
  }

  const steps = toSteps(apply);
  const existingIndex = pending.findIndex(
    (item): item is AgentStatusItem =>
      item.kind === 'agentStatus' && item.agentId === agentId,
  );
  if (existingIndex >= 0) {
    const existing = pending[existingIndex] as AgentStatusItem;
    Object.assign(existing, wrapStatusApply(agentId, steps));
    pending.splice(existingIndex, 1);
    pending.push(existing);
    return;
  }

  pending.push({
    kind: 'agentStatus',
    agentId,
    ...wrapStatusApply(agentId, steps),
  });
  armFlushTimer();
}

/**
 * Last-write-wins per queueId. Skipped while a join/left for that queue is
 * pending or in-flight so a snapshot cannot overwrite setQueueStatus.
 */
export function enqueueQueueStatusEvent(
  queueId: string,
  apply: QueueApply,
): Promise<void> | void {
  if (!isWorkspaceCoalesceEnabled()) {
    return apply();
  }

  if (hasPendingQueueDelta(queueId)) {
    return;
  }

  const epoch = currentQueueStatusEpoch(queueId);
  const existing = pending.find(
    (item): item is QueueStatusItem =>
      item.kind === 'queueStatus' && item.queueId === queueId,
  );
  if (existing) {
    existing.apply = apply;
    existing.epoch = epoch;
    return;
  }

  pending.push({ kind: 'queueStatus', queueId, apply, epoch });
  armFlushTimer();
}

/** FIFO join/left. Drops a buffered queue.status for the same queue. */
export function enqueueQueueSessionDeltaEvent(
  queueId: string,
  apply: QueueApply,
): Promise<void> | void {
  if (!isWorkspaceCoalesceEnabled()) {
    return apply();
  }

  dropPendingQueueStatus(queueId);
  pending.push({
    kind: 'queueDelta',
    queueId,
    apply,
    epoch: currentQueueDeltaEpoch(queueId),
  });
  armFlushTimer();
}
